import React, { useContext, useEffect, useState } from 'react'
import axios from 'axios'
import StockContext from '../../../../contexts/StockContext'
import TableFiltersContext from '../../../../contexts/TableFiltersContext'
import ModalContext from '../../../../contexts/ModalContext'
import UserContext from '../../../../contexts/UserContext'
import editPen from '../../../../assets/edit-pen.svg'
import lookup from '../../../../assets/certificates/Tables/lookup.svg'
import CertificatesContext from '../../../../contexts/CertificatesContext'
import SectionContext from '../../../../contexts/SectionContext'
import { ConfigButton, DivInTd, GlobalTable, HeaderLine, Td, TdButtons, Th, Tr } from '../../../../components/table-style'
import { useOutletContext } from 'react-router-dom'
import { StockOutletType } from '..'
import { TypeMainOutlet } from '../..'
import { Settings } from 'lucide-react'

interface Stock {
  codeId: number
  material: string
  certificateId:number
  format: string
  finishing: string
  fabrication: string
  heatTreatment: string
  ncm: string
  originCountry: string
  supplier: string
  stockStreet: string
  shelf: string
  stockLocation: string
  quantity: string
  totalWeight: string
  dimension1: string
  dimension2: string
  lengthInMm: string
  costPerKg: string
  costPerMm: string
  code: string
  invoice: any[]
  totalCost: string
  density: string
  certificates: any[]
  availableBalance: string
  measurementUnit: string
  totalCmc: string
  unitCmc: string
  trackingNumber: string
  family: string
  description: string
  stockStatus: string
  characteristic: string
  receivedDate: string
  purchaseOrder: string
  nf: string
  certificateNumber: string
  heatNumber: any//{certificates:any[]}
  createdAt: string
  updatedAt: string
  movimentType: string
  observation: string
  unitOfMeasurement: string
}

const StockTable: React.FC = () => {
  const [permissions, setPermissions] = useState<string[]>([])
  const { stockQuantity, details, setStock } = useContext(StockContext)
  const { stockFilters, setStockFilters } = useContext(TableFiltersContext)
  const { setCertificate,  } = useContext(CertificatesContext)
  const { search } = useOutletContext<TypeMainOutlet>()
  const [ stockList, setStockList ] = useState<Stock[]>([])
  const { setModalActive, setModalSection, setItIsUpdate } = useContext(ModalContext)
  const { user } = useContext(UserContext)
  const { setTabIndex, fields, setIsDropdownOpen, setQuantity, quantity, refresh } = useOutletContext<StockOutletType>()
  const { setStockSection } = useContext(SectionContext)

  useEffect(() => {
    setTabIndex(0)
    setStockFilters([])
    setStockSection('stock')
    setQuantity(8)
  }, [])

  useEffect(() => {
    const parameters = {
      size: quantity,
      filters: stockFilters,
      global: search,
    }
    axios
      .get(`${process.env.REACT_APP_BASE_URL}/stock/findAll`, {
        params: parameters,
      })
      .then((response) => {
        setStockList(response.data)
      })
      .catch((error) => {
        console.log('Ocorreu um erro ao obter os dados do estoque', error)
      })
  }, [quantity, stockFilters, search, refresh])

  useEffect(() => {
    const fetchPermissions = async () => {
      const perms = await getPermissions()
      setPermissions(perms) // Atualiza o estado com as permissões obtidas
    }

    if (user.profileId) {
      fetchPermissions()
    }
  }, [user.profileId])

  const formatNumber = (num: number) => {
    return num.toFixed(2)
  }

  const handleCheckCertificate = async(certificateId:number) => {
    const certificateUrl = process.env.REACT_APP_CERTIFICATES_BASE_URL
    axios.get(`${certificateUrl}/certificate/${certificateId}`)
      .then((response) => {
        setModalActive(true)
        setModalSection('viewCertificate')
        setCertificate(response.data)
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const filterStockData = (stock: Stock): Partial<Stock> => {
    const allowedFields = [
      'codeId',
      'code',
      'material',
      'format',
      'finishing',
      'fabrication',
      'heatTreatment',
      'originCountry',
      'supplier',
      'stockStreet',
      'shelf',
      'stockLocation',
      'ncm',
      'quantity',
      'totalWeight',
      'dimension1',
      'dimension2',
      'lengthInMm',
      'costPerKg',
      'costPerMm',
      'totalCost',
      'invoice',
      'density',
      'certificates',
      'availableBalance',
      'totalCmc',
      'trackingNumber',
      'receivedDate',
      'purchaseOrder',
      'nf',
      'certificateNumber',
      'heatNumber',
      'family',
      'description',
      'stockStatus',
      'characteristic',
      'measurementUnit',
    ] as const

    const filtered: any = {}
    allowedFields.forEach((key) => {
      if (key in stock) {
        filtered[key] = stock[key] ?? ''
      }
    })
    return filtered
  }

  const convertSupplierToString = (item: any) => {
    // item:Stock
    const newItem = { ...item }
    if (typeof item.supplier === 'object' && item.supplier !== null) {
      newItem.supplier = item.supplier?.name || ''
    }
    return newItem
  }

  const handleUpdateClick = (item: Stock) => {
    const filteredItem = filterStockData(item)
    const convertedSupplier = convertSupplierToString(filteredItem)
    setStock(convertedSupplier)
    setItIsUpdate(true)
    setModalActive(true)
    setModalSection('firstCustomAdd')
  }

  const handleEntryOrExit = (item: Stock) => {
    setStock(item)
    setModalActive(true)
    setModalSection('modalEntryOrExit')
  }

  const handleNewModal = (item: Stock) => {
    setStock(item)
    setModalActive(true)
    setModalSection('newModal')
  }

  const getPermissions = async () => {
    try {
      //busca as permissões com base no profileId
      const response = await axios(
        `${process.env.REACT_APP_BASE_URL}/profile/find/${user.profileId}`,
      )

      if (response.data) {
        return response.data.permissions
      }
    } catch (error: any) {
      console.log('Usuário sem perfil')
    }
  }

  const TableComponent: React.FC = () => {
    return (
      <GlobalTable className='short'>
        <thead>
          <HeaderLine>
            {fields.whichFieldsToShow.filter(field => field.checked).map((field, index) => (
              <Th key={index}>{field.fieldName}</Th>
            ))}
            <Th></Th>
            <ConfigButton onClick={() => setIsDropdownOpen(prev => !prev)}><Settings/></ConfigButton>
          </HeaderLine>
        </thead>
        <tbody>
          {stockList.map((item, itemIndex) => (
            <Tr key={item.code} className={itemIndex === 0 ? 'first' : ''}>
              {fields.whichFieldsToShow.filter(field => field.checked).map((fieldObj, index) => (
                <Td key={index}>
                    {fieldObj.field === 'description' ? (
                      <span
                        onClick={() =>
                          navigator.clipboard.writeText(
                            item[fieldObj.field as keyof Stock] as string,
                          )
                        }
                        style={{
                          cursor: 'pointer',
                        }}
                        title='Clique para copiar'
                      >
                        {item[fieldObj.field as keyof Stock]}
                      </span>
                    ) : fieldObj.field === 'lengthInMm' || fieldObj.field === 'totalWeight' ? (
                      formatNumber(
                        parseFloat(item[fieldObj.field as keyof Stock] as string),
                      )
                    ) : (
                      item[fieldObj.field as keyof Stock]
                    )}
                </Td>
              ))}
              <TdButtons>
                <img src={lookup} 
                  alt='look up icon'
                  onClick={() => handleNewModal(item)}
                  /* className={!stockList[itemIndex].certificateId ? 'withoutCertificate' : ''}
                  onClick={() => {
                    if(stockList[itemIndex].heatNumber.certificate.length > 0)
                      handleCheckCertificate(stockList[itemIndex].heatNumber.certificate[0].id)
                    return null
                  }}
                  title={stockList[itemIndex].certificateId ? 'Ver certificado' : ''} */
                />
              </TdButtons>
            </Tr>
          ))}
        </tbody>
      </GlobalTable>
    )
  }

  let tableClass = ''
  if (stockFilters.length !== 0) {
    if (details) {
      tableClass = 'filteredTable'
    } else {
      tableClass = 'filteredTable shortTable'
    }
  } else {
    if (!details) {
      tableClass = 'shortTable'
    }
  }

  return (
    <TableComponent />
  )
}

export default StockTable
